import { useState, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Loader2, AlertCircle, RefreshCw, Download, GitCompareArrows,
} from 'lucide-react';
import { apiClient } from '@/api/client';
import type { ConnectionConfig, DiffTab, QueryResult, SchemaMetadata } from '@/types';
import { qualifiedTable } from '@/lib/sql-quote';

// ═══════════════════════════════════════════
// Types
// ═══════════════════════════════════════════

interface DiffRow {
  status: 'same' | 'changed' | 'added' | 'removed';
  leftRow: Record<string, unknown> | null;
  rightRow: Record<string, unknown> | null;
  changedColumns: Set<string>;
}

type FilterMode = 'all' | 'differences' | 'added' | 'removed';

// ═══════════════════════════════════════════
// Diff algorithm
// ═══════════════════════════════════════════

function computeDiff(
  leftRows: Record<string, unknown>[],
  rightRows: Record<string, unknown>[],
  keyColumns: string[],
  compareColumns: string[],
): DiffRow[] {
  // If no key columns, match by row position
  if (keyColumns.length === 0) {
    return computePositionalDiff(leftRows, rightRows, compareColumns);
  }

  const leftMap = new Map<string, Record<string, unknown>>();
  const rightMap = new Map<string, Record<string, unknown>>();

  for (const row of leftRows) {
    const key = keyColumns.map((k) => String(row[k] ?? '')).join('|');
    leftMap.set(key, row);
  }
  for (const row of rightRows) {
    const key = keyColumns.map((k) => String(row[k] ?? '')).join('|');
    rightMap.set(key, row);
  }

  const result: DiffRow[] = [];
  const processedRight = new Set<string>();

  for (const [key, leftRow] of leftMap) {
    const rightRow = rightMap.get(key);
    if (!rightRow) {
      result.push({ status: 'removed', leftRow, rightRow: null, changedColumns: new Set() });
    } else {
      processedRight.add(key);
      const changed = new Set<string>();
      for (const col of compareColumns) {
        if (String(leftRow[col] ?? '') !== String(rightRow[col] ?? '')) {
          changed.add(col);
        }
      }
      result.push({
        status: changed.size > 0 ? 'changed' : 'same',
        leftRow,
        rightRow,
        changedColumns: changed,
      });
    }
  }

  for (const [key, rightRow] of rightMap) {
    if (!processedRight.has(key)) {
      result.push({ status: 'added', leftRow: null, rightRow, changedColumns: new Set() });
    }
  }

  return result;
}

function computePositionalDiff(
  leftRows: Record<string, unknown>[],
  rightRows: Record<string, unknown>[],
  compareColumns: string[],
): DiffRow[] {
  const result: DiffRow[] = [];
  const maxLen = Math.max(leftRows.length, rightRows.length);

  for (let i = 0; i < maxLen; i++) {
    const leftRow = leftRows[i] ?? null;
    const rightRow = rightRows[i] ?? null;

    if (!leftRow) {
      result.push({ status: 'added', leftRow: null, rightRow, changedColumns: new Set() });
    } else if (!rightRow) {
      result.push({ status: 'removed', leftRow, rightRow: null, changedColumns: new Set() });
    } else {
      const changed = new Set<string>();
      for (const col of compareColumns) {
        if (String(leftRow[col] ?? '') !== String(rightRow[col] ?? '')) {
          changed.add(col);
        }
      }
      result.push({
        status: changed.size > 0 ? 'changed' : 'same',
        leftRow,
        rightRow,
        changedColumns: changed,
      });
    }
  }

  return result;
}

// ═══════════════════════════════════════════
// Export to SQL
// ═══════════════════════════════════════════

function exportDiffAsSQL(
  diffRows: DiffRow[],
  columns: string[],
  keyColumns: string[],
  schemaTable: string,
): string {
  const lines: string[] = [];

  for (const row of diffRows) {
    if (row.status === 'added' && row.rightRow) {
      const cols = columns.filter((c) => row.rightRow![c] !== undefined);
      const vals = cols.map((c) => sqlValue(row.rightRow![c]));
      lines.push(`INSERT INTO ${schemaTable} (${cols.join(', ')}) VALUES (${vals.join(', ')});`);
    } else if (row.status === 'removed' && row.leftRow && keyColumns.length > 0) {
      const where = keyColumns.map((k) => `${k} = ${sqlValue(row.leftRow![k])}`).join(' AND ');
      lines.push(`DELETE FROM ${schemaTable} WHERE ${where};`);
    } else if (row.status === 'changed' && row.rightRow && keyColumns.length > 0) {
      const sets = Array.from(row.changedColumns).map(
        (c) => `${c} = ${sqlValue(row.rightRow![c])}`,
      );
      const where = keyColumns.map((k) => `${k} = ${sqlValue(row.leftRow![k])}`).join(' AND ');
      lines.push(`UPDATE ${schemaTable} SET ${sets.join(', ')} WHERE ${where};`);
    }
  }

  return lines.join('\n');
}

function sqlValue(val: unknown): string {
  if (val === null || val === undefined) return 'NULL';
  if (typeof val === 'number') return String(val);
  if (typeof val === 'boolean') return val ? 'TRUE' : 'FALSE';
  return `'${String(val).replace(/'/g, "''")}'`;
}

// ═══════════════════════════════════════════
// Component
// ═══════════════════════════════════════════

interface DiffViewProps {
  tab: DiffTab;
}

export function DiffView({ tab }: DiffViewProps) {
  const [schemaA, setSchemaA] = useState(tab.schemaA);
  const [tableA, setTableA] = useState(tab.tableA);
  const [schemaB, setSchemaB] = useState(tab.schemaB);
  const [tableB, setTableB] = useState(tab.tableB);
  const [matchBy, setMatchBy] = useState<'pk' | 'position'>('pk');
  const [filterMode, setFilterMode] = useState<FilterMode>('all');
  const [compareTriggered, setCompareTriggered] = useState(false);
  // Track which table/schema we actually compared (frozen at compare-click)
  const [comparedA, setComparedA] = useState({ schema: tab.schemaA, table: tab.tableA });
  const [comparedB, setComparedB] = useState({ schema: tab.schemaB, table: tab.tableB });

  const connectionIdA = tab.connectionId;
  const connectionIdB = tab.connectionIdB ?? tab.connectionId;

  // Driver type per side drives identifier quoting (Oracle folds unquoted
  // names to uppercase, so quoted refs must be uppercased). Both sides can
  // point at different connections/drivers. Subscribe to the connections
  // query rather than reading the cache so a late-arriving list re-renders.
  const { data: connections } = useQuery<ConnectionConfig[]>({
    queryKey: ['connections'],
    queryFn: () => apiClient.get<ConnectionConfig[]>('/api/connections'),
    staleTime: 60_000,
  });
  const driverTypeA = useMemo(
    () => connections?.find((c) => c.id === connectionIdA)?.driverType ?? 'postgresql',
    [connections, connectionIdA],
  );
  const driverTypeB = useMemo(
    () => connections?.find((c) => c.id === connectionIdB)?.driverType ?? 'postgresql',
    [connections, connectionIdB],
  );

  // Fetch table list for dropdowns
  const { data: schemaDataA } = useQuery({
    queryKey: ['schema', connectionIdA],
    queryFn: () => apiClient.get<SchemaMetadata>(`/api/schema/${connectionIdA}`),
    staleTime: 60_000,
  });

  const { data: schemaDataB } = useQuery({
    queryKey: ['schema', connectionIdB],
    queryFn: () => apiClient.get<SchemaMetadata>(`/api/schema/${connectionIdB}`),
    staleTime: 60_000,
    enabled: connectionIdB !== connectionIdA,
  });

  const effectiveSchemaDataB = connectionIdB === connectionIdA ? schemaDataA : schemaDataB;

  // Build flat table lists: { schema, table }[]
  const tablesA = useMemo(() => {
    if (!schemaDataA) return [];
    const result: { schema: string; table: string }[] = [];
    for (const db of schemaDataA.databases) {
      for (const s of db.schemas) {
        for (const t of s.tables) {
          result.push({ schema: s.name, table: t.name });
        }
      }
    }
    return result;
  }, [schemaDataA]);

  const tablesB = useMemo(() => {
    const data = effectiveSchemaDataB;
    if (!data) return [];
    const result: { schema: string; table: string }[] = [];
    for (const db of data.databases) {
      for (const s of db.schemas) {
        for (const t of s.tables) {
          result.push({ schema: s.name, table: t.name });
        }
      }
    }
    return result;
  }, [effectiveSchemaDataB]);

  // Fetch data for both tables when compare is triggered
  const {
    data: dataA,
    isLoading: loadingA,
    error: errorA,
  } = useQuery({
    queryKey: ['diff-data-a', connectionIdA, driverTypeA, comparedA.schema, comparedA.table, compareTriggered],
    queryFn: () =>
      apiClient.post<QueryResult>('/api/query/execute', {
        connectionId: connectionIdA,
        sql: `SELECT * FROM ${qualifiedTable(comparedA.schema, comparedA.table, driverTypeA)}`,
        page: 1,
        pageSize: 10000,
      }),
    enabled: compareTriggered,
  });

  const {
    data: dataB,
    isLoading: loadingB,
    error: errorB,
  } = useQuery({
    queryKey: ['diff-data-b', connectionIdB, driverTypeB, comparedB.schema, comparedB.table, compareTriggered],
    queryFn: () =>
      apiClient.post<QueryResult>('/api/query/execute', {
        connectionId: connectionIdB,
        sql: `SELECT * FROM ${qualifiedTable(comparedB.schema, comparedB.table, driverTypeB)}`,
        page: 1,
        pageSize: 10000,
      }),
    enabled: compareTriggered,
  });

  const isLoading = loadingA || loadingB;
  const error = errorA || errorB;

  // Determine key columns from primary keys
  const keyColumns = useMemo(() => {
    if (!dataA || matchBy === 'position') return [];
    return dataA.columns.filter((c) => c.isPrimaryKey).map((c) => c.name);
  }, [dataA, matchBy]);

  // All columns from both sides
  const allColumns = useMemo(() => {
    if (!dataA && !dataB) return [];
    const colsA = dataA?.columns.map((c) => c.name) ?? [];
    const colsB = dataB?.columns.map((c) => c.name) ?? [];
    // Union preserving order from A, then any new from B
    const set = new Set(colsA);
    const result = [...colsA];
    for (const c of colsB) {
      if (!set.has(c)) {
        result.push(c);
        set.add(c);
      }
    }
    return result;
  }, [dataA, dataB]);

  // Compute diff
  const diffRows = useMemo(() => {
    if (!dataA || !dataB) return [];
    const compareColumns = allColumns.filter((c) => !keyColumns.includes(c));
    return computeDiff(dataA.rows, dataB.rows, keyColumns, compareColumns.length > 0 ? compareColumns : allColumns);
  }, [dataA, dataB, keyColumns, allColumns]);

  // Filter
  const filteredRows = useMemo(() => {
    switch (filterMode) {
      case 'differences':
        return diffRows.filter((r) => r.status !== 'same');
      case 'added':
        return diffRows.filter((r) => r.status === 'added');
      case 'removed':
        return diffRows.filter((r) => r.status === 'removed');
      default:
        return diffRows;
    }
  }, [diffRows, filterMode]);

  // Summary stats
  const summary = useMemo(() => {
    const total = diffRows.length;
    const changed = diffRows.filter((r) => r.status === 'changed').length;
    const added = diffRows.filter((r) => r.status === 'added').length;
    const removed = diffRows.filter((r) => r.status === 'removed').length;
    const same = diffRows.filter((r) => r.status === 'same').length;
    return { total, changed, added, removed, same };
  }, [diffRows]);

  const handleCompare = useCallback(() => {
    setComparedA({ schema: schemaA, table: tableA });
    setComparedB({ schema: schemaB, table: tableB });
    setCompareTriggered((v) => !v); // toggle to force refetch
  }, [schemaA, tableA, schemaB, tableB]);

  const handleExport = useCallback(() => {
    if (diffRows.length === 0) return;
    const sql = exportDiffAsSQL(
      diffRows.filter((r) => r.status !== 'same'),
      allColumns,
      keyColumns,
      `${comparedB.schema}.${comparedB.table}`,
    );
    if (!sql) return;
    const blob = new Blob([sql], { type: 'text/sql' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `diff_${comparedA.table}_${comparedB.table}.sql`;
    a.click();
    URL.revokeObjectURL(url);
  }, [diffRows, allColumns, keyColumns, comparedA.table, comparedB.table, comparedB.schema]);

  const handleSelectA = useCallback(
    (value: string) => {
      const [s, t] = value.split('||');
      setSchemaA(s);
      setTableA(t);
    },
    [],
  );

  const handleSelectB = useCallback(
    (value: string) => {
      const [s, t] = value.split('||');
      setSchemaB(s);
      setTableB(t);
    },
    [],
  );

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Toolbar */}
      <div className="flex flex-col gap-2 p-3 border-b border-[var(--border-default)] bg-dbgo-panel flex-shrink-0">
        {/* Row 1: Table selectors */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-text-muted font-medium w-10">Left:</span>
          <select
            value={`${schemaA}||${tableA}`}
            onChange={(e) => handleSelectA(e.target.value)}
            className="text-xs bg-dbgo-workarea border border-[var(--border-default)] rounded px-2 py-1 text-text-primary min-w-[200px]"
          >
            {tablesA.map((t) => (
              <option key={`${t.schema}.${t.table}`} value={`${t.schema}||${t.table}`}>
                {t.schema}.{t.table}
              </option>
            ))}
          </select>

          <GitCompareArrows size={16} className="text-text-muted mx-1" />

          <span className="text-xs text-text-muted font-medium w-10">Right:</span>
          <select
            value={`${schemaB}||${tableB}`}
            onChange={(e) => handleSelectB(e.target.value)}
            className="text-xs bg-dbgo-workarea border border-[var(--border-default)] rounded px-2 py-1 text-text-primary min-w-[200px]"
          >
            {tablesB.map((t) => (
              <option key={`${t.schema}.${t.table}`} value={`${t.schema}||${t.table}`}>
                {t.schema}.{t.table}
              </option>
            ))}
          </select>

          <span className="text-xs text-text-muted font-medium ml-2">Match by:</span>
          <select
            value={matchBy}
            onChange={(e) => setMatchBy(e.target.value as 'pk' | 'position')}
            className="text-xs bg-dbgo-workarea border border-[var(--border-default)] rounded px-2 py-1 text-text-primary"
          >
            <option value="pk">Primary Key</option>
            <option value="position">Row Position</option>
          </select>

          <button
            onClick={handleCompare}
            disabled={isLoading}
            className="flex items-center gap-1.5 px-3 py-1 text-xs font-medium bg-accent-blue text-white rounded hover:bg-accent-blue/90 disabled:opacity-50 ml-2"
          >
            {isLoading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            Compare
          </button>

          {diffRows.length > 0 && (
            <button
              onClick={handleExport}
              className="flex items-center gap-1.5 px-3 py-1 text-xs font-medium border border-[var(--border-default)] rounded text-text-secondary hover:text-text-primary hover:bg-dbgo-tab-hover"
            >
              <Download size={12} />
              Export SQL
            </button>
          )}
        </div>

        {/* Row 2: Filter buttons */}
        {diffRows.length > 0 && (
          <div className="flex items-center gap-1">
            <span className="text-xs text-text-muted font-medium mr-1">Show:</span>
            <FilterButton label="All" count={summary.total} active={filterMode === 'all'} onClick={() => setFilterMode('all')} />
            <FilterButton label="Differences" count={summary.changed + summary.added + summary.removed} active={filterMode === 'differences'} onClick={() => setFilterMode('differences')} />
            <FilterButton label="Added" count={summary.added} active={filterMode === 'added'} color="green" onClick={() => setFilterMode('added')} />
            <FilterButton label="Removed" count={summary.removed} active={filterMode === 'removed'} color="red" onClick={() => setFilterMode('removed')} />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {!compareTriggered && (
          <div className="flex flex-col items-center justify-center h-full text-text-muted">
            <GitCompareArrows size={32} className="mb-3 opacity-30" />
            <p className="text-sm">Select two tables and click Compare</p>
          </div>
        )}

        {isLoading && (
          <div className="flex items-center justify-center h-full gap-2 text-text-muted">
            <Loader2 size={16} className="animate-spin" />
            <span className="text-sm">Loading data...</span>
          </div>
        )}

        {error && (
          <div className="flex items-center justify-center h-full gap-2 text-accent-red">
            <AlertCircle size={16} />
            <span className="text-sm">{error instanceof Error ? error.message : 'Error fetching data'}</span>
          </div>
        )}

        {compareTriggered && !isLoading && !error && dataA && dataB && (
          <DiffTable
            rows={filteredRows}
            columns={allColumns}
            keyColumns={keyColumns}
            leftLabel={`${comparedA.schema}.${comparedA.table}`}
            rightLabel={`${comparedB.schema}.${comparedB.table}`}
          />
        )}
      </div>

      {/* Summary bar */}
      {diffRows.length > 0 && !isLoading && (
        <div className="flex items-center gap-4 px-3 py-1.5 border-t border-[var(--border-default)] bg-dbgo-panel flex-shrink-0 text-xs text-text-muted">
          <span>{summary.total} total</span>
          <span className="text-text-primary">{summary.same} same</span>
          <span className="text-accent-orange">{summary.changed} changed</span>
          <span className="text-accent-green">{summary.added} added</span>
          <span className="text-accent-red">{summary.removed} removed</span>
          {matchBy === 'pk' && keyColumns.length > 0 && (
            <span className="ml-auto opacity-60">Matched by: {keyColumns.join(', ')}</span>
          )}
          {matchBy === 'pk' && keyColumns.length === 0 && (
            <span className="ml-auto opacity-60 text-accent-yellow">No PK found, using row position</span>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════
// Filter Button
// ═══════════════════════════════════════════

function FilterButton({
  label,
  count,
  active,
  color,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  color?: 'green' | 'red';
  onClick: () => void;
}) {
  let colorClass = 'text-text-secondary';
  if (color === 'green') colorClass = 'text-accent-green';
  if (color === 'red') colorClass = 'text-accent-red';

  return (
    <button
      onClick={onClick}
      className={`px-2 py-0.5 text-xs rounded border transition-colors ${
        active
          ? 'border-accent-blue bg-accent-blue/10 text-accent-blue'
          : `border-[var(--border-default)] hover:bg-dbgo-tab-hover ${colorClass}`
      }`}
    >
      {label} ({count})
    </button>
  );
}

// ═══════════════════════════════════════════
// Diff Table (side-by-side)
// ═══════════════════════════════════════════

function DiffTable({
  rows,
  columns,
  keyColumns,
  leftLabel,
  rightLabel,
}: {
  rows: DiffRow[];
  columns: string[];
  keyColumns: string[];
  leftLabel: string;
  rightLabel: string;
}) {
  return (
    <div className="flex h-full overflow-hidden">
      {/* Left side */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden border-r border-[var(--border-default)]">
        <div className="px-3 py-1.5 text-xs font-semibold text-text-primary bg-dbgo-panel border-b border-[var(--border-default)] flex-shrink-0">
          {leftLabel}
        </div>
        <div className="flex-1 overflow-auto">
          <table className="w-full text-xs border-collapse">
            <thead className="sticky top-0 z-10">
              <tr className="bg-dbgo-panel">
                <th className="px-2 py-1 text-left font-medium text-text-muted border-b border-[var(--border-default)] w-8">#</th>
                {columns.map((col) => (
                  <th
                    key={col}
                    className={`px-2 py-1 text-left font-medium border-b border-[var(--border-default)] whitespace-nowrap ${
                      keyColumns.includes(col) ? 'text-accent-yellow' : 'text-text-muted'
                    }`}
                  >
                    {col}
                    {keyColumns.includes(col) && <span className="ml-1 text-[10px]">PK</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i} className={rowBgClass(row.status, 'left')}>
                  <td className="px-2 py-0.5 text-text-muted border-b border-[var(--border-subtle)] w-8 text-right">
                    {i + 1}
                  </td>
                  {columns.map((col) => (
                    <td
                      key={col}
                      className={`px-2 py-0.5 border-b border-[var(--border-subtle)] whitespace-nowrap max-w-[200px] truncate ${
                        cellBgClass(row, col, 'left')
                      }`}
                    >
                      {row.leftRow ? formatCellValue(row.leftRow[col]) : ''}
                    </td>
                  ))}
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={columns.length + 1} className="px-4 py-8 text-center text-text-muted">
                    No rows to display
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Right side */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <div className="px-3 py-1.5 text-xs font-semibold text-text-primary bg-dbgo-panel border-b border-[var(--border-default)] flex-shrink-0">
          {rightLabel}
        </div>
        <div className="flex-1 overflow-auto">
          <table className="w-full text-xs border-collapse">
            <thead className="sticky top-0 z-10">
              <tr className="bg-dbgo-panel">
                <th className="px-2 py-1 text-left font-medium text-text-muted border-b border-[var(--border-default)] w-8">#</th>
                {columns.map((col) => (
                  <th
                    key={col}
                    className={`px-2 py-1 text-left font-medium border-b border-[var(--border-default)] whitespace-nowrap ${
                      keyColumns.includes(col) ? 'text-accent-yellow' : 'text-text-muted'
                    }`}
                  >
                    {col}
                    {keyColumns.includes(col) && <span className="ml-1 text-[10px]">PK</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i} className={rowBgClass(row.status, 'right')}>
                  <td className="px-2 py-0.5 text-text-muted border-b border-[var(--border-subtle)] w-8 text-right">
                    {i + 1}
                  </td>
                  {columns.map((col) => (
                    <td
                      key={col}
                      className={`px-2 py-0.5 border-b border-[var(--border-subtle)] whitespace-nowrap max-w-[200px] truncate ${
                        cellBgClass(row, col, 'right')
                      }`}
                    >
                      {row.rightRow ? formatCellValue(row.rightRow[col]) : ''}
                    </td>
                  ))}
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={columns.length + 1} className="px-4 py-8 text-center text-text-muted">
                    No rows to display
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════
// Styling helpers
// ═══════════════════════════════════════════

function rowBgClass(status: DiffRow['status'], side: 'left' | 'right'): string {
  switch (status) {
    case 'added':
      return side === 'right' ? 'bg-accent-green/10' : 'bg-[var(--dbgo-workarea)] opacity-40';
    case 'removed':
      return side === 'left' ? 'bg-accent-red/10' : 'bg-[var(--dbgo-workarea)] opacity-40';
    case 'changed':
      return 'bg-accent-orange/5';
    default:
      return '';
  }
}

function cellBgClass(row: DiffRow, col: string, side: 'left' | 'right'): string {
  if (row.status === 'changed' && row.changedColumns.has(col)) {
    return side === 'left' ? 'bg-accent-red/15 text-text-primary' : 'bg-accent-green/15 text-text-primary';
  }
  return 'text-text-secondary';
}

function formatCellValue(val: unknown): string {
  if (val === null || val === undefined) return 'NULL';
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val);
}
